import React, { useState, useEffect, KeyboardEventHandler } from 'react'
import { withStyles, createStyles } from '@mui/styles'
import { InputAdornment, TextField as MuiTextField } from '@mui/material'
import { muiOptions, defaultColors, MuiProps } from '../../../infrastructure/materialUiThemeProvider'
import { t } from '../../../infrastructure/i18nextHelper'
import { BaseFieldProps } from '../fieldProps'
import { fieldStatuses } from '../styles'

type TextFieldStyleProps = {
    root?
}

type TextFieldProps = {
    label?: string
    text?: string | number | null
    defaultValue?: string
    onChange?: (e) => void
    onKeyDown?: KeyboardEventHandler<any>
    maxLength?: number
    inputProps?: any
    auto?: boolean
    trim?: boolean
    fullWidth?: boolean
    password?: boolean
    helperText?: string
    disableNewStyle?: boolean
    hideUnderline?: boolean
    overrideStyle?: TextFieldStyleProps
} & BaseFieldProps

function _TextField(props: TextFieldProps & MuiProps) {

    let { status, classes, id, label, text, defaultValue, onChange, onKeyDown, maxLength, inputProps, auto, trim, fullWidth, password, helperText,
        disableNewStyle, hideUnderline, overrideStyle, disabled, hideTitle, onError } = props

    let fixValue = (x) => {
        let result = String(x ?? defaultValue ?? '')
        if (trim)
            result = result.trim()

        return result
    }

    let isReadOnly = disabled
    let highlight = status === 'info' ? classes.fieldStatusInfo
        : status === 'warning' ? classes.fieldStatusWarning : ''

    let variant = !disableNewStyle ? { variant: 'filled' } as any : {}

    return (
        <MuiTextField
            {...variant}
            className={`${hideTitle ? classes.hideLabel : '' + ' ' + highlight} 
                ${overrideStyle?.root ?? (fullWidth ? classes.textFieldDesignLine : classes.textFieldDesign)}`}
            id={id}
            label={label}
            hiddenLabel={!label}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: maxLength }}
            InputProps={Object.assign({ disableUnderline: hideUnderline || isReadOnly, readOnly: isReadOnly, 'data-testid': id }, inputProps)}
            error={status === 'alert' || !!onError}
            type={password ? 'password' : 'text'}
            value={fixValue(text)}
            onChange={onChange}
            helperText={status === 'alert' || onError ? t('components.error') : auto ? t('components.autoCalculated') : helperText ?? null}
            onKeyDown={onKeyDown}
            onClick={e => e.stopPropagation()}
            disabled={isReadOnly}
        />
    )
}

export type NumberFieldProps = {
    label?: string,
    unit?: string | null
    text?: number | null
    inputProps?: any
    max?: number
    min?: number
    maxLength?: number
    auto?: boolean
    allowNegative?: boolean
    precision?: number
    errorMessage?: string
    disableNewStyle?: boolean
    hideUnderline?: boolean
    overrideStyle?: TextFieldStyleProps
    align?: 'right' | 'left' | 'center'
    size?: 'small' | 'standard' | 'medium'
    onChange?: (e: number | null) => void
    onFocus?: () => void
    onBlur?: () => void
    onKeyDown?: KeyboardEventHandler<any>
    inputRef?: any
} & BaseFieldProps

let _NumberField = (props: NumberFieldProps & MuiProps) => {
    let { classes, id, label, unit, text, inputProps, max, min, maxLength, auto, allowNegative, precision, errorMessage, disableNewStyle,
        hideUnderline, overrideStyle, align, size, onChange, onFocus, onBlur, onKeyDown, inputRef, disabled, onError, status, hideTitle } = props
    let [value, setValue] = useState<string>(text + '')

    useEffect(() => { setValue(text + '') }, [text])

    let handleUnsigned = (x) => allowNegative ? parseFloat(x) : Math.abs(parseFloat(x))
    let fixPrecision = (x: number) => precision != null ? parseFloat(x.toFixed(precision)) : x
    let minMax = (input: number) => min != null && input < min ? min
        : max != null && input > max ? max
            : input
    let fixValue = (x: number | null): number | null => {
        let value = handleUnsigned(x)
        value = fixPrecision(value)
        return !isNaN(value) ? fixPrecision(minMax(value)) : null
    }

    let lastCharIsDecimalSeparator = (text: string) => text !== '' && text?.indexOf('.') === text.length - 1
    let newValueIsDifferentFromPrevious = (text: string) => Number.parseFloat(text) !== Number.parseFloat(value)
    let lastCharIsZeroAfterDecimalSeparator = (text: string) => (text !== '' && text?.indexOf('.') !== -1 && text?.lastIndexOf('0') === text.length - 1)
    let onlyCharIsNegativeSign = (text: string) => allowNegative && text.length == 1 && text.indexOf('-') == 0

    let shouldRaiseChange = (text: string) =>
        !lastCharIsDecimalSeparator(text) &&
        (newValueIsDifferentFromPrevious(text) || !lastCharIsZeroAfterDecimalSeparator(text))
        && !onlyCharIsNegativeSign(text)

    let replaceComma = (text: string) => replaceAll(text, ',', '.')
    let replaceSpaces = (text: string) => replaceAll(text, ' ', '')
    let formatText = (text: string) => replaceComma(replaceSpaces(text))

    let inputText = formatText(shouldRaiseChange(value) ? (fixValue(parseFloat(value)) ?? '') + '' : value)

    let variant = !disableNewStyle ? { variant: 'filled' } as any : {}

    let highlightClass = status === 'info' ? classes.fieldStatusInfo : status === 'warning' ? classes.fieldStatusWarning : ''
    let sizeClass = size === 'standard' ? classes.standard : size === 'small' ? classes.small : size === 'medium' ? classes.medium : ''

    return (
        <MuiTextField
            id={id}
            inputRef={inputRef}
            label={label}
            hiddenLabel={!label}
            variant={disableNewStyle ? 'standard' : 'filled'}
            className={`${hideTitle ? classes.hideLabel : ''} 
                ${sizeClass} ${highlightClass} ${overrideStyle?.root ?? classes.textFieldDesign}`}
            error={status === 'alert' || !!onError}
            value={inputText}
            onKeyDown={onKeyDown}
            onChange={x => {
                let input = formatText(x.target.value ?? '')
                setValue(max && parseFloat(input) > max
                    ? max + ''
                    : min && parseFloat(input) < min
                        ? min + ''
                        : input)
                if (shouldRaiseChange(input) && onChange)
                    onChange(fixValue(parseFloat(input)))
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ style: { textAlign: align ?? 'left' }, maxLength: maxLength ?? 10, 'data-testid': id }}
            InputProps={unit
                ? { disableUnderline: hideUnderline || disabled, readOnly: disabled, endAdornment: <InputAdornment position='end'>{unit}</InputAdornment> }
                : { disableUnderline: hideUnderline || disabled, readOnly: disabled, ...inputProps }}
            helperText={status === 'alert' || onError
                ? errorMessage
                    ? errorMessage
                    : t('components.error')
                : auto
                    ? t('components.autoCalculated')
                    : null}
            onClick={e => e.stopPropagation()}
            onFocus={onFocus ?? (() => { })}
            onBlur={onBlur ?? (() => { })}
            disabled={disabled} />
    )
}

function replaceAll(text: string, search: string, replace: string) {
    return text.split(search).join(replace)
}

let styles = _ =>
    createStyles({
        textFieldDesign: {
            width: '15.3em',
            margin: '0em 0.5em',
            '& .Mui-focused:not(.Mui-disabled):before': {
                borderBottomColor: defaultColors.darkBlue.main.color,
                borderBottomStyle: 'solid',
                borderBottomWidth: '2px',
            },
            '& label.Mui-focused:not(.Mui-error)': {
                color: defaultColors.darkBlue.main.color,
            },
            '& .MuiFilledInput-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.05) !important',
                '& .Mui-disabled': {
                    backgroundColor: 'white',
                    color: defaultColors.inputText
                }
            },
            '& .MuiInputBase-root': {
                '& .Mui-disabled': {
                    backgroundColor: 'white',
                    color: defaultColors.inputText
                }
            },
            '& .MuiInputBase-input.MuiFilledInput-input.Mui-disabled': {
                color: 'black !important',
                WebkitTextFillColor: 'black !important',
                backgroundColor: 'white !important',
                border: 'none !important'
            },
            '& .MuiInputLabel-filled.MuiInputLabel-shrink': {
                fontSize: '0.75em !important',
                transform: 'translate(12px,10px) !important'
            },
            '& .MuiInputLabel-shrink': {
                fontSize: '0.75em !important',
                transform: 'translate(0px,0px) !important'
            }
        },
        textFieldDesignLine: {
            width: '98%',
            margin: '0em 0.5em',
            '& .Mui-focused:not(.Mui-disabled):before': {
                borderBottomColor: defaultColors.darkBlue.main.color,
                borderBottomStyle: 'solid',
                borderBottomWidth: '2px',
            },
            '& label.Mui-focused:not(.Mui-error)': {
                color: defaultColors.darkBlue.main.color,
            },
            '& .MuiFilledInput-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.05) !important',
                '& .Mui-disabled': {
                    backgroundColor: 'white',
                    color: defaultColors.inputText
                }
            },
            '& .MuiInputBase-root': {
                '& .Mui-disabled': {
                    backgroundColor: 'white',
                    color: defaultColors.inputText
                }
            },
            '& .MuiInputBase-input.MuiFilledInput-input.Mui-disabled': {
                color: 'black !important',
                WebkitTextFillColor: 'black !important',
                backgroundColor: 'white !important',
                border: 'none !important'
            },
            '& .MuiInputLabel-filled.MuiInputLabel-shrink': {
                fontSize: '0.75em !important',
                transform: 'translate(12px,10px) !important'
            },
            '& .MuiInputLabel-shrink': {
                fontSize: '0.75em !important',
                transform: 'translate(0px,0px) !important'
            }
        },
        hideLabel: {
            marginTop: '0',
            '& label': {
                display: 'none'
            },
            '& div': {
                marginTop: '0'
            }
        },
        standard: {
            width: '10.3em',
            margin: '0em 0.5em'
        },
        small: {
            width: '6em',
            margin: '0em 0.5em'
        },
        medium: {
            width: '6.8em',
            margin: '0em 0.5em'
        },
        ...fieldStatuses
    })

export let TextField = withStyles(styles, muiOptions)(_TextField)
export let NumberField = withStyles(styles, muiOptions)(_NumberField)